import { APIClient } from './api-client'
import { GIFT_VALUE } from './config'
import { Customer, Theme } from './schemas'

export interface EmbeddedModalConfig {
  customer: Customer
  partnerName?: string
  theme?: Theme
  clientId: string
  code: string
  apiUrl: string
  partnerLogoSvg?: string
  referralCode?: string
  skipOffer?: boolean
  showClose?: boolean
}

type ModalState = 'offer' | 'nift-flow'

export class EmbeddedModal {
  private container: HTMLDivElement | null = null
  private config: EmbeddedModalConfig
  private onCloseCallback?: () => void
  private onSelectionCallback?: () => void
  private messageListener?: (event: MessageEvent) => void
  private currentState: ModalState = 'offer'
  private apiClient: APIClient
  private actualNiftCardCode?: string
  private triggerElement: Element | null = null
  private focusTrapListener?: (e: KeyboardEvent) => void

  constructor(config: EmbeddedModalConfig) {
    this.config = config
    this.apiClient = new APIClient()

    this.loadCustomFonts()
  }

  private getStyles(): string {
    const theme = this.config.theme || {}

    const modalWidth = theme.modal_width ? `${theme.modal_width}px` : '600px'
    const borderRadius = theme.border_radius != null ? `${theme.border_radius}px` : '16px'
    const buttonRadius = theme.button_radius != null ? `${theme.button_radius}px` : '50px'

    return `
      <style>
        .nift-embedded-modal-overlay {
          position: fixed;
          box-sizing: border-box;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          height: 100dvh;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 999999;
          animation: nift-fade-in 0.2s ease-out;
        }

        .nift-embedded-modal-overlay div {
          box-sizing: border-box;
        }

        .nift-embedded-modal-wrapper {
          position: relative;
          width: 90%;
          max-width: ${modalWidth};
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .nift-modal-close-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin-bottom: 8px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.2s;
          flex-shrink: 0;
        }

        .nift-modal-close-button:hover {
          opacity: 0.8;
        }

        .nift-modal-close-button svg {
          width: 24px;
          height: 24px;
        }

        .nift-embedded-modal-container {
          background-color: #F9F9F9;
          border-radius: ${borderRadius};
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          width: 100%;
          max-height: calc(90vh - 40px);
          max-height: calc(90dvh - 40px);
          position: relative;
          animation: nift-slide-up 0.3s ease-out;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        @supports (-webkit-touch-callout: none) {
          .nift-embedded-modal-container {
            max-height: -webkit-fill-available;
          }
        }

        .nift-embedded-modal-header {
          background: transparent;
          padding: 16px 20px;
          display: none;
          justify-content: flex-end;
          align-items: center;
          position: absolute;
          top: 0;
          right: 0;
          z-index: 10;
        }

        .nift-embedded-modal-title {
          font-size: 18px;
          font-weight: 600;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
          display: none;
        }

        .nift-embedded-modal-close {
          background: none;
          border: none;
          font-size: 28px;
          font-weight: 300;
          cursor: pointer;
          color: ${theme.header_color || '#101B21'};
          width: 32px;
          height: 32px;
          display: none;
          align-items: center;
          justify-content: center;
          padding: 0;
          line-height: 1;
          transition: opacity 0.2s;
        }

        .nift-embedded-modal-close:hover {
          opacity: 0.7;
        }

        .nift-embedded-modal-content {
          flex: 1;
          position: relative;
          overflow: auto;
        }

        .nift-embedded-iframe {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
        }

        .nift-embedded-loading {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10;
          transition: opacity 0.3s;
        }

        .nift-embedded-loading.hidden {
          opacity: 0;
          pointer-events: none;
        }

        .nift-embedded-loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e5e7eb;
          border-top-color: ${theme.primary_color || '#101B21'};
          border-radius: 50%;
          animation: nift-spin 1s linear infinite;
          margin-bottom: 16px;
        }

        .nift-embedded-loading-text {
          color: #6b7280;
          font-size: 14px;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
        }

        @keyframes nift-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes nift-slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes nift-spin {
          to { transform: rotate(360deg); }
        }

        .nift-offer-container {
          display: flex;
          flex-direction: column;
          min-height: 400px;
          height: 100%;
          padding: 0px 24px;
          background: white;
          overflow: hidden;
        }

        .nift-offer-header {
          background: transparent;
          padding: 16px 20px;
          display: none;
          justify-content: flex-end;
          align-items: center;
          position: absolute;
          top: 0;
          right: 0;
          z-index: 10;
        }

        .nift-offer-logos {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          width: 100%;
        }

        .nift-partner-logo {
          height: 40px;
          width: auto;
          max-width: 200px;
          object-fit: contain;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          border-radius: 8px;
        }

        .nift-partner-logo svg {
          height: 40px;
          width: auto;
          display: block;
        }

        .nift-powered-logo {
          max-height: 36px;
          max-width: 41px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        .nift-powered-logo svg {
          height: 48px;
          width: auto;
          display: block;
        }

        .nift-modal-image {
          width: 100%;
          margin-top: 16px;
          display: flex;
          justify-content: center;
        }

        .nift-modal-image img {
          max-height: 130px;
          width: auto;
          height: auto;
          display: block;
        }

        .nift-offer-content {
          flex: 1;
          padding: 24px 0 0 0;
          display: flex;
          flex-direction: column;
          align-items: ${theme.text_alignment === 'center' ? 'center' : theme.text_alignment === 'right' ? 'flex-end' : 'flex-start'};
          text-align: ${theme.text_alignment || 'left'};
          width: 100%;
          overflow-y: auto;
          min-height: 0;
        }

        .nift-offer-intro {
          font-size: 18px;
          color: #4b5563;
          margin-bottom: 28px;
          line-height: 1.4;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
        }

        .nift-gift-badge {
          background: ${theme.primary_color || '#e65425'};
          color: white;
          padding: 12px 28px;
          border-radius: 30px;
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 24px;
          display: inline-block;
        }

        .nift-offer-headline {
          font-size: 24px;
          font-weight: 800;
          color: ${theme.header_color || theme.text_color || '#111827'};
          margin-bottom: 8px;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
          text-align: ${theme.text_alignment || 'left'};
        }

        .nift-offer-description {
          font-size: 14px;
          color: ${theme.text_color || '#6b7280'};
          line-height: 1.5;
          margin-bottom: 16px;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
          text-align: ${theme.text_alignment || 'left'};
        }

        .nift-offer-expiration {
          font-size: 14px;
          color: ${theme.text_color || '#6b7280'};
          line-height: 1.5;
          margin-bottom: 16px;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
          text-align: ${theme.text_alignment || 'left'};
        }

        .nift-button-group {
          width: 100%;
          margin-top: auto;
          flex-shrink: 0;
          padding: 16px 0;
          background: white;
          position: sticky;
          bottom: 0;
        }

        .nift-claim-button {
          width: 100%;
          background: ${theme.header_color || '#111827'};
          color: ${theme.button_text_color || 'white'};
          border: none;
          padding: 18px 32px;
          border-radius: ${buttonRadius};
          font-size: 18px;
          font-weight: 600;
          height: 64px;
          cursor: pointer;
          transition: all 0.2s;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
        }

        .nift-claim-button:hover {
          background: ${theme.hover_color || '#000000'};
        }

        .nift-claim-button:active {
          transform: scale(0.98);
        }

        .nift-decline-button {
          display: none;
        }

        .nift-offer-footer {
          padding: 8px 0 32px 0;
          padding-bottom: max(32px, env(safe-area-inset-bottom, 32px));
          text-align: center;
          color: ${theme.text_color || '#5f6566'};
          font-size: 10px;
          flex-shrink: 0;
          font-family: ${theme.font_family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
        }

        .nift-offer-footer a {
          color: ${theme.link_color || theme.primary_color || '#e65425'};
          text-decoration: none;
        }

        .nift-offer-footer a:hover {
          text-decoration: underline;
        }

        .nift-sdk-container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .nift-embedded-modal-container:has(.nift-sdk-container) {
          height: 90vh;
          height: 90dvh;
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .nift-embedded-modal-overlay {
            align-items: flex-end;
            padding-bottom: 0;
          }

          .nift-embedded-modal-wrapper {
            width: calc(100%);
            max-width: 100%;
            max-height: calc(100vh - 8px);
            max-height: calc(100dvh - 8px);
          }

          .nift-modal-close-button {
            margin-bottom: 6px;
            margin-right: 8px;
            flex-shrink: 0;
          }

          .nift-embedded-modal-container {
            width: 100%;
            max-width: 100%;
            max-height: calc(100vh - 40px);
            max-height: calc(100dvh - 40px);
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }

          .nift-offer-container {
            padding: 0 16px;
          }

          .nift-embedded-modal-container:has(.nift-sdk-container) {
            height: calc(100vh - 40px);
            height: calc(100dvh - 40px);
            max-height: calc(100vh - 40px);
            max-height: calc(100dvh - 40px);
          }

          .nift-sdk-container {
            height: 100%;
            min-height: auto;
          }

          .nift-offer-container {
            min-height: auto;
          }

          .nift-offer-logos {
            flex-shrink: 0;
          }

          .nift-offer-content {
            padding: 24px 0 0 0;
          }

          .nift-offer-intro {
            font-size: 16px;
          }

          .nift-gift-badge {
            font-size: 20px;
            padding: 10px 24px;
          }

          .nift-offer-headline {
            font-size: 24px;
          }

          .nift-offer-description {
            font-size: 16px;
          }

          .nift-offer-footer {
            flex-shrink: 0;
          }
        }

        .nift-claim-button:focus-visible,
        .nift-decline-button:focus-visible,
        .nift-modal-close-button:focus-visible,
        .nift-embedded-modal-close:focus-visible {
          outline: 3px solid #1a73e8;
          outline-offset: 2px;
        }

        .nift-claim-button:focus:not(:focus-visible),
        .nift-decline-button:focus:not(:focus-visible),
        .nift-modal-close-button:focus:not(:focus-visible),
        .nift-embedded-modal-close:focus:not(:focus-visible) {
          outline: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .nift-embedded-modal-overlay,
          .nift-embedded-modal-container,
          .nift-embedded-loading-spinner {
            animation: none !important;
          }
          .nift-claim-button,
          .nift-decline-button,
          .nift-modal-close-button,
          .nift-embedded-modal-close,
          .nift-embedded-loading {
            transition: none !important;
          }
        }
      </style>
    `
  }

  private getExpirationDate(): string {
    const expirationDate = new Date()
    expirationDate.setDate(expirationDate.getDate() + 30)

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }

    return expirationDate.toLocaleDateString('en-US', options)
  }

  private replaceTemplateVariables(text: string): string {
    const { customer } = this.config

    const smartReplace = (str: string, pattern: RegExp, value: string | undefined): string => {
      if (value) {
        return str.replace(pattern, value)
      }
      const patternStr = pattern.source
      const cleanupPattern = new RegExp(`[,\\s]*${patternStr}[,\\s]*`, 'gi')
      return str.replace(cleanupPattern, '')
    }

    let result = text
    result = smartReplace(result, /\{first_name\}/gi, customer.firstName)
    result = smartReplace(result, /\{last_name\}/gi, customer.lastName)
    result = smartReplace(result, /\{email\}/gi, customer.email)

    return result
  }

  private processEmojis(text: string): string {
    const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu
    return text.replace(emojiRegex, '<span style="font-size: 1.4em;">$1</span>')
  }

  private buildNiftFlowURL(): string {
    const { customer, clientId, apiUrl } = this.config
    const code = this.actualNiftCardCode || this.config.code

    const params = new URLSearchParams()
    if (customer.firstName) params.append('first_name', customer.firstName)
    if (customer.lastName) params.append('last_name', customer.lastName)
    if (customer.email) params.append('email', customer.email)
    if (customer.phoneNumber) params.append('phone_number', customer.phoneNumber)
    if (clientId) params.append('client_id', clientId)

    const url = `${apiUrl}/nift_cards/${code}/start?${params.toString()}`
    return url
  }

  private getOfferHTML(): string {
    const { theme } = this.config
    const rawTitle = theme?.modal_title || `You've earned yourself a gift!`
    const rawDescription =
      theme?.modal_description ||
      `Enjoy a $${GIFT_VALUE} gift card on us. Use it to try something new — wine, music, food delivery and more.`
    const buttonText = theme?.modal_button_text || 'Claim your gift'
    const partnerLogo = theme?.partner_logo || ''
    const modalImage = theme?.modal_image || ''

    const title = this.replaceTemplateVariables(rawTitle)
    const description = this.processEmojis(this.replaceTemplateVariables(rawDescription))

    const niftLogoSvg = `<svg width="41" height="36" viewBox="0 0 41 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M5.01883 35.9421C3.75626 35.9421 2.66957 34.9188 2.49368 33.5693L0.0250673 14.4843C-0.169658 13.0005 0.791407 11.6446 2.16705 11.4591L35.6661 6.96287C37.0669 6.7774 38.3169 7.85828 38.5053 9.32292L40.974 28.4079C41.0682 29.0987 40.9049 29.8022 40.5154 30.3778C40.126 30.9598 39.5167 31.3436 38.832 31.4331L5.33291 35.9229C5.2324 35.9357 5.12562 35.9421 5.01883 35.9421Z" fill="#F04B28"/>
<path d="M35.8471 0.647563L34.4708 5.73069L33.7689 5.8231L34.2252 4.14115L32.5099 1.08691L33.2654 0.987453L34.5163 3.3645L35.1451 0.739976L35.8471 0.647563Z" fill="#F04B28"/>
<path d="M29.6073 1.96519C29.7014 1.79142 29.8411 1.64191 30.0263 1.51666C30.215 1.38692 30.4323 1.30587 30.6782 1.2735C30.9677 1.23538 31.2386 1.27033 31.4908 1.37832C31.7431 1.48632 31.9536 1.66033 32.1224 1.90036C32.2906 2.13642 32.3964 2.41902 32.4397 2.74818C32.4831 3.07734 32.4545 3.38167 32.3542 3.66116C32.2533 3.93669 32.0935 4.16348 31.8749 4.34154C31.6597 4.51512 31.4093 4.6207 31.1238 4.65829C30.87 4.6917 30.6374 4.67189 30.4261 4.59885C30.2187 4.52529 30.0473 4.41875 29.912 4.27922L29.9746 4.75512L29.2965 4.8444L28.7169 0.442383L29.3951 0.353103L29.6073 1.96519ZM31.7497 2.83903C31.7199 2.61298 31.6468 2.42492 31.5302 2.27486C31.417 2.1203 31.2776 2.01157 31.112 1.94865C30.9498 1.88124 30.7815 1.85902 30.607 1.88199C30.4365 1.90444 30.2796 1.96947 30.1364 2.07708C29.9972 2.18417 29.891 2.32726 29.8177 2.50635C29.7485 2.68493 29.7287 2.88724 29.7585 3.11329C29.7882 3.33933 29.8599 3.53162 29.9736 3.69014C30.0907 3.84417 30.2303 3.95489 30.3925 4.0223C30.5587 4.08918 30.727 4.1114 30.8975 4.08895C31.072 4.06598 31.2289 4.00095 31.3681 3.89386C31.5108 3.78229 31.6165 3.63523 31.6852 3.45269C31.758 3.26963 31.7795 3.06508 31.7497 2.83903Z" fill="#F04B28"/>
<path d="M23.2759 3.95458C23.2325 3.62542 23.2616 3.32506 23.363 3.0535C23.4684 2.78142 23.6289 2.56057 23.8447 2.39097C24.0638 2.21687 24.3181 2.11077 24.6076 2.07266C24.8218 2.04446 25.0382 2.06438 25.257 2.13241C25.4792 2.19595 25.6619 2.29696 25.8052 2.43544L25.5969 0.853095L26.281 0.763032L26.8605 5.16505L26.1764 5.25511L26.1114 4.76137C26.0213 4.93462 25.8839 5.08585 25.6991 5.21507C25.5184 5.34376 25.3011 5.42482 25.0473 5.45823C24.7618 5.49582 24.4906 5.4589 24.2339 5.34746C23.9806 5.23153 23.7676 5.05381 23.5948 4.81431C23.4255 4.57032 23.3192 4.28374 23.2759 3.95458ZM25.9603 3.61328C25.9305 3.38723 25.8571 3.19719 25.74 3.04316C25.6269 2.8886 25.4892 2.77762 25.327 2.71021C25.1648 2.64281 24.9965 2.62059 24.822 2.64356C24.6475 2.66653 24.4906 2.73156 24.3514 2.83865C24.2117 2.94177 24.1052 3.08288 24.0319 3.26197C23.9621 3.43658 23.9421 3.63691 23.9719 3.86295C24.0016 4.089 24.0733 4.28129 24.187 4.43981C24.3046 4.59781 24.4448 4.71249 24.6075 4.78387C24.7737 4.85075 24.942 4.87297 25.1125 4.85052C25.287 4.82754 25.4439 4.76252 25.5831 4.65543C25.7223 4.54834 25.8266 4.40551 25.8958 4.22694C25.9686 4.04388 25.9901 3.83933 25.9603 3.61328Z" fill="#F04B28"/>
<path d="M22.8259 3.94733C22.8421 4.07027 22.8488 4.18236 22.846 4.28359L20.3416 4.6133C20.3959 4.87243 20.5207 5.06983 20.7161 5.20548C20.9115 5.34114 21.1361 5.39227 21.3899 5.35885C21.7547 5.31082 21.9924 5.1242 22.1029 4.79899L22.8346 4.70266C22.7751 5.01712 22.6273 5.28873 22.3911 5.51752C22.1584 5.74181 21.8497 5.87928 21.465 5.92992C21.1517 5.97117 20.861 5.93884 20.5929 5.83293C20.3283 5.72253 20.1078 5.54982 19.9316 5.31481C19.7589 5.07531 19.6503 4.78701 19.606 4.44992C19.5616 4.11283 19.5901 3.8085 19.6915 3.53694C19.7964 3.26089 19.9626 3.03728 20.1902 2.86611C20.4218 2.69441 20.6982 2.58742 21.0194 2.54513C21.3288 2.50441 21.6133 2.53554 21.8729 2.63853C22.1326 2.74151 22.346 2.90707 22.5132 3.1352C22.6799 3.35936 22.7841 3.63007 22.8259 3.94733ZM22.0898 3.82638C22.053 3.57705 21.9374 3.38853 21.743 3.2608C21.5487 3.13308 21.3226 3.08618 21.0649 3.12012C20.8309 3.15092 20.6405 3.25264 20.4938 3.42527C20.3466 3.59393 20.2738 3.80725 20.2755 4.06524L22.0898 3.82638Z" fill="#F04B28"/>
<path d="M18.0744 3.4714C18.1516 3.29179 18.2655 3.14567 18.4161 3.03305C18.5701 2.91594 18.7602 2.84251 18.9862 2.81275L19.0786 3.51469L18.9061 3.5374C18.6404 3.57238 18.447 3.66643 18.326 3.81954C18.2089 3.97213 18.1723 4.21499 18.2161 4.54811L18.4433 6.27323L17.7651 6.3625L17.3336 3.08479L18.0117 2.99551L18.0744 3.4714Z" fill="#F04B28"/>
<path d="M16.8714 4.73127C16.8876 4.85421 16.8943 4.96629 16.8915 5.06753L14.3871 5.39724C14.4414 5.65637 14.5662 5.85376 14.7616 5.98942C14.957 6.12508 15.1816 6.1762 15.4354 6.14279C15.8002 6.09475 16.0379 5.90813 16.1484 5.58293L16.8801 5.4866C16.8206 5.80105 16.6728 6.07267 16.4367 6.30145C16.204 6.52574 15.8953 6.66321 15.5106 6.71386C15.1973 6.7551 14.9066 6.72277 14.6385 6.61686C14.3738 6.50647 14.1534 6.33376 13.9772 6.09875C13.8044 5.85924 13.6959 5.57095 13.6515 5.23386C13.6071 4.89676 13.6356 4.59244 13.7371 4.32088C13.8419 4.04483 14.0082 3.82122 14.2358 3.65004C14.4673 3.47835 14.7437 3.37136 15.065 3.32907C15.3743 3.28834 15.6588 3.31947 15.9185 3.42246C16.1781 3.52545 16.3916 3.69101 16.5588 3.91913C16.7254 4.14329 16.8297 4.41401 16.8714 4.73127ZM16.1354 4.61031C16.0985 4.36099 15.9829 4.17247 15.7886 4.04474C15.5942 3.91701 15.3682 3.87012 15.1104 3.90405C14.8764 3.93486 14.6861 4.03657 14.5393 4.2092C14.3921 4.37786 14.3193 4.59119 14.321 4.84918L16.1354 4.61031Z" fill="#F04B28"/>
<path d="M13.1554 3.63494L12.5697 7.04658L11.8558 7.14055L10.8768 4.80638L10.5352 7.31442L9.82139 7.40839L8.3667 4.26538L9.05674 4.17453L10.0878 6.71973L10.4368 3.99284L11.1447 3.89965L12.1564 6.4353L12.4891 3.72265L13.1554 3.63494Z" fill="#F04B28"/>
<path d="M6.82024 7.85785C6.51091 7.89857 6.22218 7.86598 5.95406 7.76007C5.68542 7.6502 5.46301 7.47775 5.28683 7.24274C5.11013 7.00376 4.99958 6.71572 4.9552 6.37863C4.91135 6.04551 4.94582 5.7404 5.05861 5.4633C5.17141 5.18621 5.34558 4.96155 5.58112 4.78934C5.81665 4.61712 6.09107 4.51039 6.40436 4.46914C6.71766 4.4279 7.01035 4.45997 7.28243 4.56535C7.55452 4.67074 7.7809 4.84266 7.96157 5.08112C8.14224 5.31958 8.2545 5.60537 8.29836 5.9385C8.34221 6.27162 8.30576 6.57699 8.189 6.85461C8.07224 7.13222 7.89238 7.35965 7.64944 7.53687C7.40993 7.70961 7.13353 7.8166 6.82024 7.85785ZM6.7427 7.26893C6.9172 7.24596 7.07431 7.18291 7.21405 7.07979C7.35775 6.97615 7.46623 6.83478 7.53948 6.65569C7.61273 6.47659 7.63369 6.26807 7.60236 6.03013C7.57104 5.79218 7.49906 5.59791 7.38644 5.44732C7.2733 5.29277 7.1359 5.18377 6.97424 5.12033C6.81257 5.05689 6.64449 5.03665 6.47 5.05963C6.2955 5.0826 6.13839 5.14565 5.99865 5.24877C5.86288 5.35137 5.76234 5.49169 5.69702 5.66974C5.63118 5.84382 5.61392 6.04984 5.64525 6.28778C5.69172 6.64074 5.81697 6.90263 6.02102 7.07346C6.2285 7.2398 6.46907 7.30496 6.7427 7.26893Z" fill="#F04B28"/>
<path d="M1.6699 5.63726C1.76506 5.47142 1.90501 5.32389 2.08972 5.19468C2.27443 5.06546 2.49171 4.98441 2.74156 4.95152C3.02709 4.91392 3.29625 4.95111 3.54903 5.06307C3.80525 5.17055 4.01748 5.34232 4.18573 5.57838C4.35399 5.81443 4.45978 6.09704 4.50311 6.4262C4.54645 6.75536 4.51793 7.05969 4.41755 7.33918C4.31665 7.61471 4.15688 7.8415 3.93826 8.01956C3.72307 8.19314 3.47271 8.29872 3.18717 8.33631C2.93733 8.3692 2.70872 8.34887 2.50134 8.27531C2.29345 8.19778 2.11811 8.09176 1.97533 7.95724L2.24317 9.99169L1.56502 10.081L0.928313 5.2447L1.60646 5.15542L1.6699 5.63726ZM3.81307 6.51705C3.78331 6.291 3.71014 6.10294 3.59355 5.95288C3.48041 5.79832 3.34102 5.68958 3.17539 5.62667C3.01321 5.55926 2.84487 5.53704 2.67037 5.56001C2.49984 5.58246 2.34299 5.64749 2.19981 5.7551C2.0606 5.86219 1.95437 6.00528 1.88112 6.18437C1.81183 6.36294 1.79207 6.56525 1.82183 6.7913C1.85159 7.01735 1.9233 7.20964 2.03696 7.36816C2.15407 7.52219 2.29372 7.63291 2.4559 7.70032C2.62206 7.7672 2.7904 7.78942 2.96092 7.76697C3.13542 7.744 3.29227 7.67897 3.43149 7.57188C3.57414 7.46031 3.67985 7.31325 3.74862 7.13071C3.82134 6.94765 3.84283 6.7431 3.81307 6.51705Z" fill="#F04B28"/>
<path d="M37.897 18.9598C37.9032 19.4559 37.5127 19.8668 37.0253 19.8731C36.5379 19.8794 36.1342 19.4818 36.128 18.9857C36.1218 18.4896 36.5123 18.0787 36.9997 18.0724C37.2366 18.0682 37.4653 18.1616 37.6336 18.3302C37.803 18.492 37.8996 18.7197 37.897 18.9598ZM37.7718 18.9608C37.7752 18.537 37.4339 18.1849 37.0164 18.1883C36.599 18.1917 36.2541 18.5322 36.2575 18.9571C36.2541 19.3809 36.5954 19.733 37.0129 19.7296C37.2138 19.7337 37.4072 19.6517 37.5482 19.5047C37.6892 19.3577 37.7745 19.1664 37.7718 18.9608ZM37.434 19.4792L37.267 19.4806L37.0649 19.0378L36.7795 19.0413L36.7797 19.4869L36.6262 19.4904L36.6213 18.4499L37.0523 18.4487C37.1504 18.4433 37.2432 18.4724 37.316 18.5407C37.3705 18.592 37.4005 18.6675 37.4022 18.7456C37.4004 18.8019 37.385 18.856 37.3561 18.9079C37.3204 18.9587 37.2655 18.9994 37.2069 19.0182L37.434 19.4792ZM36.7853 18.9149L37.0493 18.915C37.1046 18.9169 37.1631 18.898 37.2034 18.8621C37.237 18.825 37.2581 18.7789 37.252 18.7285C37.2528 18.6791 37.2321 18.6333 37.1946 18.606C37.1572 18.5788 37.1108 18.5642 37.0623 18.5635L36.7768 18.5669L36.7853 18.9149Z" fill="white"/>
<path d="M35.317 17.8904L33.6843 17.7793C34.7638 17.3545 35.9489 16.1188 35.7944 14.945C35.694 14.1828 35.0368 14.0158 34.4589 14.0919C33.4694 14.2222 32.6903 15.1718 32.1734 16.2437C31.8339 14.5106 30.6038 13.7244 29.8732 13.8206C29.1426 13.9168 28.8318 14.6214 28.973 15.5559C29.0681 16.1859 29.5061 16.7479 29.7153 16.9858C29.7202 16.9915 29.7237 16.9952 29.7249 16.9967C29.7859 17.0746 30.0294 17.3255 30.2525 17.5457L29.0894 17.4667C29.031 17.4628 28.9748 17.4818 28.9308 17.5202C28.8868 17.5586 28.8606 17.612 28.8569 17.6703L28.6783 20.4821L28.6568 20.5525C28.6491 20.5783 28.6454 20.6079 28.6346 20.6398C28.2675 21.7316 27.4036 23.767 26.4974 23.8499C25.9789 23.8973 25.8352 23.4535 25.7244 22.6122C25.6523 22.0645 25.6866 21.2828 25.7665 20.4712C26.2661 20.2242 26.7282 19.9638 27.1315 19.7076C27.2422 19.6372 27.2804 19.5837 27.2875 19.4841C27.2949 19.3808 27.2971 19.3544 27.3001 19.2812C27.3052 19.1565 27.2376 19.0777 27.0542 19.1753C26.6404 19.3958 26.2392 19.5924 25.8453 19.7683C26.0246 18.3305 26.2928 16.9826 26.3139 16.8554C26.3376 16.7125 26.2946 16.6051 26.1876 16.5687L25.4773 16.3271C25.3845 16.2955 25.2349 16.2992 25.1988 16.469C24.9608 17.592 24.7184 18.9984 24.5903 20.2673C23.4352 20.6695 22.3006 20.9058 21.0507 21.0482C22.709 20.0475 24.1931 17.3958 23.8662 14.912C23.8213 14.5717 23.6692 13.4864 22.6028 13.6268C20.7462 13.8712 19.8634 18.5817 19.7599 22.57C19.5787 24.1023 18.91 24.8603 18.461 24.9194C18.012 24.9786 17.7949 24.5386 17.7545 24.2319C17.6282 23.2721 17.8121 22.0307 17.9767 20.7071C17.9865 20.6282 17.9848 20.4596 17.8402 20.4106L17.1181 20.1668C16.9901 20.1232 16.8597 20.2122 16.8428 20.3506C16.7871 20.8088 16.6655 21.7255 16.6003 22.589C16.4874 23.7096 16.0546 25.2392 15.3084 25.3374C15.0521 25.3712 14.7536 25.2244 14.6518 24.5322C14.4954 23.4708 14.8608 22.3627 14.7455 21.4866C14.6612 20.8468 14.2405 20.3271 13.6453 20.427C12.979 20.5387 12.3197 21.2673 11.7263 22.6699C11.7559 22.3751 11.7955 21.971 11.7455 21.5906C11.6652 20.9807 11.1247 20.5563 10.5367 20.6337C9.13383 20.8184 7.15449 22.86 5.86833 23.0294C5.39388 23.0918 5.04817 22.9138 4.67118 22.3079C4.60949 22.2087 4.50262 22.1628 4.37566 22.2361C4.2487 22.3094 4.0385 22.4316 3.93765 22.4986C3.83679 22.5655 3.8136 22.6612 3.90823 22.79C4.23498 23.2349 4.8944 23.7524 5.82737 23.6296C7.47976 23.4121 9.09004 21.5123 10.262 21.358C10.473 21.3302 10.5641 21.4572 10.5837 21.6068C10.7718 23.0351 10.478 25.0501 10.3568 25.9375C10.3221 26.1911 10.3927 26.2267 10.5342 26.2748L11.2485 26.5156C11.3314 26.5437 11.473 26.5164 11.489 26.3486C11.7792 23.3144 12.9522 21.2526 13.3857 21.1956C13.4957 21.1811 13.5581 21.2825 13.5774 21.4292C13.6519 21.9945 13.3167 23.5321 13.4502 24.5462C13.5613 25.3898 13.9593 26.1815 15.1756 26.0214C16.0022 25.9125 16.4667 25.2216 16.6973 24.6147C16.9249 25.2819 17.4475 25.7225 18.3517 25.6034C19.0959 25.5054 19.5302 24.9216 19.7684 24.3825C19.792 25.0048 19.8375 25.5819 19.9041 26.0876C20.2195 28.4832 21.0767 29.3189 22.1506 29.1775C23.0898 29.0538 23.9125 27.492 23.3895 24.8789C23.165 23.7573 22.5543 22.4185 21.8051 21.761C22.7117 21.6366 23.6478 21.3643 24.5273 21.0197C24.4854 21.6704 24.4847 22.2547 24.5439 22.7049C24.6959 23.8591 25.2507 24.6951 26.3259 24.5536C27.2195 24.436 28.0996 23.3966 28.5596 22.3463L28.4825 23.5599C28.4749 23.6794 28.566 23.7832 28.6854 23.7912L34.9131 24.2145C34.9278 24.2156 34.9424 24.2151 34.9569 24.2132C34.9991 24.2076 35.0389 24.1898 35.0717 24.161C35.1157 24.1226 35.142 24.0693 35.1456 24.0109L35.5196 18.1216C35.5272 18.0021 35.4361 17.8983 35.3168 17.8902L35.317 17.8904ZM22.9163 14.4746C23.015 14.4616 23.1173 14.5004 23.1831 14.8879C23.4124 16.24 22.7144 18.8733 21.1378 20.0841C21.1917 19.4219 21.9554 14.6011 22.9165 14.4746L22.9163 14.4746ZM22.6345 25.5046C22.7831 26.6331 22.794 28.4811 22.1459 28.5664C21.8308 28.6079 21.2459 27.6332 21.0285 25.9819C20.8981 24.9912 20.8369 23.5047 20.9807 21.8851C21.7745 22.3488 22.4904 24.4096 22.6345 25.5046ZM34.897 14.6702C35.1188 14.641 35.2381 14.7633 35.2723 14.919C35.4651 15.7971 33.9572 17.3264 32.9028 17.4959C33.276 16.2644 34.0971 14.7755 34.897 14.6702ZM29.9685 14.3332C30.4492 14.27 31.4216 15.117 31.6597 16.6585C31.7039 16.9448 31.7045 17.1891 31.6936 17.4055C30.9765 16.8718 29.7964 15.685 29.6924 14.8948C29.6424 14.5155 29.7827 14.3579 29.9685 14.3335L29.9685 14.3332ZM30.0141 18.1524L31.5932 18.2588L31.5934 18.2588L31.2693 23.3471L29.689 23.2396L30.0143 18.1524L30.0141 18.1524ZM33.9901 23.532L32.4116 23.4247L32.7364 18.3358L34.3123 18.442L33.9899 23.5318L33.9901 23.532Z" fill="white"/>
<path d="M17.6476 19.0171C18.0325 18.9894 18.334 18.6941 18.3213 18.3577C18.3086 18.0213 17.9862 17.7712 17.6013 17.7989C17.2163 17.8267 16.9148 18.1219 16.9275 18.4583C16.9403 18.7947 17.2626 19.0448 17.6476 19.0171Z" fill="white"/>
</svg>
`

    const isSvgContent = partnerLogo.trim().startsWith('<svg')

    let svgContent = partnerLogo
    if (isSvgContent) {
      svgContent = partnerLogo.replace(/<svg([^>]*)\s(width|height)=["'][^"']*["']/g, '<svg$1')
      svgContent = svgContent.replace(/<svg([^>]*)\s(width|height)=["'][^"']*["']/g, '<svg$1')
    }

    const partnerLogoHtml = partnerLogo
      ? isSvgContent
        ? `<div class="nift-partner-logo">${svgContent}</div>`
        : `<img src="${partnerLogo}" alt="Partner Logo" class="nift-partner-logo" />`
      : '<div></div>'

    return `
      <div class="nift-offer-container" id="nift-offer-container">
        <div class="nift-offer-header">
          <span id="nift-customer-greeting"></span>
          <button class="nift-embedded-modal-close" aria-label="Close" id="nift-offer-close">×</button>
        </div>

        <div class="nift-offer-logos">
          ${partnerLogoHtml}
          <div class="nift-powered-logo">${niftLogoSvg}</div>
        </div>

        ${modalImage ? `<div class="nift-modal-image"><img src="${modalImage}" alt="" /></div>` : ''}

        <div class="nift-offer-content">

          <h2 class="nift-offer-headline" id="nift-modal-headline">${title}</h2>

          <p class="nift-offer-description">
            ${description}
          </p>

          ${
            theme?.show_expiration !== false
              ? `<p class="nift-offer-expiration">
            Claim your gift by <strong>${this.getExpirationDate()}</strong>
          </p>`
              : ''
          }

          <div class="nift-button-group">
            <button class="nift-claim-button" id="nift-claim-button">
              ${buttonText}
            </button>
            <button class="nift-decline-button" id="nift-decline-button">
              No Thanks
            </button>
          </div>
        </div>

        <div class="nift-offer-footer">
          Gifts powered by <a href="https://www.gonift.com" target="_blank">Nift</a> • <a href="https://www.gonift.com/privacy" style="text-decoration: underline;" target="_blank">Privacy Policy</a>
        </div>
      </div>
    `
  }

  private getNiftFlowHTML(): string {
    const { partnerName } = this.config
    const title = partnerName ? `${partnerName} - Nift Rewards` : 'Nift Rewards'
    const iframeUrl = this.buildNiftFlowURL()

    return `
      <div class="nift-sdk-container" id="nift-sdk-container">
        <div class="nift-embedded-modal-header">
          <div class="nift-embedded-modal-title" id="nift-modal-headline">${title}</div>
          <button class="nift-embedded-modal-close" aria-label="Close" id="nift-sdk-close">×</button>
        </div>
        <div class="nift-embedded-modal-content">
          <div class="nift-embedded-loading" id="nift-embedded-loading" role="status" aria-live="polite">
            <div class="nift-embedded-loading-spinner" aria-hidden="true"></div>
            <div class="nift-embedded-loading-text">Loading your gift...</div>
          </div>
          <iframe
            id="nift-embedded-iframe"
            class="nift-embedded-iframe"
            src="${iframeUrl}"
            title="Nift Rewards"
            allow="clipboard-write"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            aria-busy="true"
          ></iframe>
        </div>
      </div>
    `
  }

  private getLoadingHTML(): string {
    return `
      <div class="nift-offer-container" id="nift-loading-container" style="display: flex; align-items: center; justify-content: center; min-height: 400px;" role="status" aria-live="polite">
        <div style="text-align: center;">
          <div class="nift-embedded-loading-spinner" style="margin: 0 auto 16px;" aria-hidden="true"></div>
          <span class="nift-embedded-loading-text">Loading...</span>
        </div>
      </div>
    `
  }

  private getCloseButtonSvg(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path fill-rule="evenodd" clip-rule="evenodd" d="M12 0C5.3673 0 0 5.3649 0 12C0 18.6327 5.3673 24 12 24C18.6351 24 24 18.6327 24 12C24 5.3637 18.6363 0 12 0ZM7.425 6.9C7.44727 6.89883 7.46836 6.89883 7.49063 6.9C7.65234 6.89883 7.80821 6.96211 7.92188 7.07813L12.0001 11.1467L16.0687 7.07813C16.1777 6.9668 16.3253 6.90352 16.4812 6.9C16.7296 6.88945 16.9593 7.03477 17.0566 7.26327C17.155 7.49296 17.1011 7.75779 16.9218 7.93125L12.8436 12.0095L16.9218 16.0781C17.0355 16.1905 17.0988 16.3441 17.0988 16.5046C17.0988 16.664 17.0355 16.8175 16.9218 16.9312C16.8093 17.0437 16.6558 17.1081 16.4952 17.1081C16.3347 17.1081 16.1823 17.0437 16.0687 16.9312L12.0001 12.853L7.92187 16.9312C7.68632 17.1667 7.30429 17.1667 7.06873 16.9312C6.83317 16.6956 6.83318 16.3136 7.06873 16.0781L11.1373 12.0095L7.06873 7.93125C6.89881 7.76836 6.83904 7.52226 6.91639 7.30077C6.99256 7.07929 7.1918 6.92226 7.425 6.9Z" fill="white"/>
</svg>`
  }

  private getHTML(): string {
    const content = this.config.skipOffer ? this.getLoadingHTML() : this.getOfferHTML()
    const closeButton = this.config.showClose
      ? `<button class="nift-modal-close-button" aria-label="Close" id="nift-modal-close-button">
            ${this.getCloseButtonSvg()}
          </button>`
      : ''
    return `
      ${this.getStyles()}
      <div class="nift-embedded-modal-overlay" id="nift-embedded-modal-overlay">
        <div class="nift-embedded-modal-wrapper" role="dialog" aria-modal="true" aria-labelledby="nift-modal-headline">
          ${closeButton}
          <div class="nift-embedded-modal-container">
            ${content}
          </div>
        </div>
      </div>
    `
  }

  private setupMessageListener(): void {
    const allowedOrigin = new URL(this.config.apiUrl).origin

    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) return

      if (event.data && event.data.type === 'close-nift-modal') {
        this.close()
      }

      if (event.data && event.data.type === 'nift-open-link') {
        window.open(event.data.url, '_blank')
      }

      if (event.data && event.data.type === 'nift-gift-selected') {
        this.onSelectionCallback?.()
      }
    }

    window.addEventListener('message', this.messageListener)
  }

  private async transitionToNiftFlow(): Promise<void> {
    const modalContainer = this.container?.querySelector('.nift-embedded-modal-container')
    if (!modalContainer) {
      console.error('Embedded Modal: Modal container not found')
      return
    }

    try {
      // Use referralCode from activations response if available, otherwise use the original code
      const codeToConfirm = this.config.referralCode || this.config.code
      const confirmation = await this.apiClient.confirmDetails(codeToConfirm, this.config.customer)
      this.actualNiftCardCode = confirmation.code

      modalContainer.innerHTML = this.getNiftFlowHTML()
      this.currentState = 'nift-flow'

      this.attachNiftFlowListeners()
    } catch (error) {
      console.error('Embedded Modal: Error during transition:', error)
      modalContainer.innerHTML = this.getNiftFlowHTML()
      this.currentState = 'nift-flow'
      this.attachNiftFlowListeners()
    }
  }

  private attachOfferListeners(): void {
    const closeButton = this.container?.querySelector('#nift-offer-close')
    const modalCloseButton = this.container?.querySelector('#nift-modal-close-button')
    const claimButton = this.container?.querySelector('#nift-claim-button')
    const declineButton = this.container?.querySelector('#nift-decline-button')
    const overlay = this.container?.querySelector('#nift-embedded-modal-overlay')

    closeButton?.addEventListener('click', () => this.close())
    modalCloseButton?.addEventListener('click', () => this.close())
    declineButton?.addEventListener('click', () => {
      this.close()
    })

    claimButton?.addEventListener('click', async () => {
      await this.apiClient.sendEvent('claim').catch(console.warn)
      this.transitionToNiftFlow()
    })

    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.close()
      }
    })
  }

  private attachNiftFlowListeners(): void {
    const closeButton = this.container?.querySelector('#nift-sdk-close')
    const modalCloseButton = this.container?.querySelector('#nift-modal-close-button')
    const overlay = this.container?.querySelector('#nift-embedded-modal-overlay')
    const iframe = this.container?.querySelector('#nift-embedded-iframe') as HTMLIFrameElement

    closeButton?.addEventListener('click', () => this.close())
    modalCloseButton?.addEventListener('click', () => this.close())

    overlay?.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.close()
      }
    })

    if (iframe) {
      iframe.addEventListener('load', () => {
        iframe.setAttribute('aria-busy', 'false')
        const loadingOverlay = this.container?.querySelector('#nift-embedded-loading')
        setTimeout(() => {
          loadingOverlay?.classList.add('hidden')
        }, 500)
      })
    }
  }

  private attachEventListeners(): void {
    if (this.currentState === 'offer') {
      this.attachOfferListeners()
    } else {
      this.attachNiftFlowListeners()
    }

    document.addEventListener('keydown', this.handleEscKey)

    this.focusTrapListener = (e: KeyboardEvent) => this.trapFocus(e)
    document.addEventListener('keydown', this.focusTrapListener)
  }

  private getVisibleFocusableElements(): HTMLElement[] {
    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
    const wrapper = this.container?.querySelector('.nift-embedded-modal-wrapper')
    if (!wrapper) return []

    return Array.from(wrapper.querySelectorAll(focusableSelector)).filter(
      (el) => (el as HTMLElement).offsetWidth > 0 && (el as HTMLElement).offsetHeight > 0
    ) as HTMLElement[]
  }

  private trapFocus(e: KeyboardEvent): void {
    if (e.key !== 'Tab') return

    const focusableElements = this.getVisibleFocusableElements()
    if (focusableElements.length === 0) return

    const firstFocusable = focusableElements[0]
    const lastFocusable = focusableElements[focusableElements.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault()
        lastFocusable.focus()
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault()
        firstFocusable.focus()
      }
    }
  }

  private handleEscKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.close()
    }
  }

  show(onClose?: () => void, onSelection?: () => void): void {
    this.onCloseCallback = onClose
    this.onSelectionCallback = onSelection

    this.triggerElement = document.activeElement

    this.container = document.createElement('div')
    this.container.innerHTML = this.getHTML()
    document.body.appendChild(this.container)

    this.attachEventListeners()
    this.setupMessageListener()

    document.body.style.overflow = 'hidden'

    requestAnimationFrame(() => {
      const focusableElements = this.getVisibleFocusableElements()
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      }
    })

    if (this.config.skipOffer) {
      this.transitionToNiftFlow()
    }
  }

  close(): void {
    if (this.container) {
      document.removeEventListener('keydown', this.handleEscKey)

      if (this.focusTrapListener) {
        document.removeEventListener('keydown', this.focusTrapListener)
      }

      if (this.messageListener) {
        window.removeEventListener('message', this.messageListener)
      }

      this.container.remove()
      this.container = null

      document.body.style.overflow = ''

      if (this.triggerElement && this.triggerElement instanceof HTMLElement) {
        this.triggerElement.focus()
      }
      this.triggerElement = null

      if (this.onCloseCallback) {
        this.onCloseCallback()
      }
    }
  }

  isOpen(): boolean {
    return this.container !== null
  }

  private loadCustomFonts = () => {
    const theme = this.config.theme
    if (!theme?.fonts?.length) return

    const fontFaces = theme.fonts.map(
      (themeFont) =>
        new FontFace(themeFont.font_family, `url("${themeFont.src}")`, {
          style: themeFont.font_style || 'normal',
          weight: `${themeFont.font_weight}`,
          stretch: themeFont.font_stretch || 'normal',
        })
    )

    fontFaces.forEach((fontFace) => {
      document.fonts.add(fontFace)
    })
  }
}
